import React from 'react'
import { Bulb, Code } from '../icons/Icons'
import {
  IconFiles,
  IconPolaroid,
  IconFolderRoot,
  IconPencil,
  IconWorldSearch
} from '@tabler/icons-react'
import { staticAssetsPath } from '../../../../configs/constants'
import { areArraysEqual, hasImages } from './TextUtils'

export const fileCountLimit = 10
export const fileSizeLimit = 50

const responseSchema = {
  id: '',
  content: null,
  sources: [], //source of content or data
  suggestions: [],
  model: null
}

const messageSchema = {
  messageId: '',
  response: [responseSchema],
  files: [],
  role: 'assistant',
  type: 'text',
  isRegenerating: false
}

const promptGuideSrc =
  'https://savingsandinvestments.sharepoint.com/:b:/r/sites/MGAnalytics551/SiteAssets/Centrali/Ali%20-%20Prompt%20guide.pdf?csf=1&web=1&e=Bqx0A6'

const everyDayQuesArray = [
  'What are the symptoms of the common cold?',
  'Tell me about famous tourist attractions in London',
  'How do I make a classic spaghetti bolognese?',
  'How can I improve my credit score?',
  "What's the difference between Type 1 and Type 2 diabetes? ",
  'Provide tips for taking care of indoor houseplants.',
  'What are some tips for better time management?',
  'Can you recommend me a good book in the mystery genre?',
  'What are some healthy and easy-to-make breakfast ideas?',
  'Prepare a 30 day workout plan for strength training'
]

const workRelatedQuesArray = [
  'Can you explain the basics of digital marketing?',
  'Explain the agile methodology in project management.',
  'How can I improve my professional networking skills?',
  'What are some strategies for resolving conflicts in a team?',
  'What are the key elements of a well-organized project plan?',
  'How can I stay organized with multiple ongoing projects?',
  'Provide tips for writing a clear and concise business email.',
  'What are the steps to conducting a successful client meeting?',
  'Help me with some strategies for improving workplace communication.',
  'How can I enhance my leadership skills?',
  'Give me tips for delivering a compelling presentation.',
  'Explain the basics of data analysis and visualization.',
  'What are some negotiation tactics for successful business deals?',
  'What are the best practices for remote work productivity?'
]

const welcomePageSuggestions = [
  {
    id: 1,
    icon: <IconPencil />,
    title: 'Create a summary from a meeting transcript',
    description: `
      [Attach Meeting Transcript]
      Please provide a comprehensive summary of the attached meeting transcript.
      Additional details to enhance the summary:
      - Background context: [Background reasons for the meeting]
      - Summary focus areas: [Specify if you need emphasis on aspects like decisions, action items, or technical discussions]
      
      Please structure the summary with:
      - Key decisions made
      - Action items with owners
      - Main discussion points
      - Notable concerns or risks raised
      - Next steps
      - Timeline commitments
      
      Highlight any time-sensitive or critical items that require immediate attention.
    `
  },
  {
    id: 2,
    icon: <Code size={28} />,
    title: 'Generate an excel formula to achieve a given task',
    description: `
      Help me create an Excel formula for the following requirement:
  
      Task description:
      [Describe what you want the formula to accomplish]
  
      Relevant details:
      - Data layout: [Describe your column headers and data format]
      - Specific conditions: [List any conditions or criteria the formula needs to consider]
      - Expected output: [Describe what the result should look like]
  
      Additional requirements:
      - Error handling needs: [Specify if you need specific error handling]
  
      Please provide the formula with:
      - Step-by-step breakdown of how it works
      - Examples of its usage
    `
  },
  {
    id: 3,
    icon: <Bulb />,
    title: 'Build a pros and cons analysis for my decision',
    description: `
      Help me create a comprehensive pros and cons analysis for this situation/decision.
  
      Deliverables:
  
      Context:
      - Decision/situation to analyze: [Describe what needs to be evaluated]
      - Strategy: [Describe any strategy this decision relates to]
      - Key stakeholders: [Who is affected by this decision?]
      - Current situation: [Describe status quo]
  
      Analysis Parameters:
      - Impact areas to consider: [Financial/operational/strategic/etc.]
      - Short-term vs long-term effects: [Specify any known impacts]
      - Risk tolerance: [High/medium/low]
      - Resource constraints: [List any limiting factors]
  
      Please analyze with these categories:
      - Immediate benefits and drawbacks
      - Long-term implications
      - Financial impacts
      - Operational effects
      - People/cultural impacts
      - Risk considerations
      - Implementation challenges
      - Strategic alignment
  
      Additional requirements:
      - Weight/prioritize the factors
      - Identify critical decision points
      - Suggest mitigation strategies for main drawbacks
    `
  },
  {
    id: 4,
    icon: <Bulb />,
    title: 'Provide different stakeholder perspectives',
    description: `
      Please analyze this situation from different stakeholder perspectives:
  
      Situation Overview:
      [Describe the situation or change being considered]
  
      Key Context:
      - Business area affected: [Specify department/function]
      - Timeline: [Immediate/short-term/long-term impact]
      - Key implications: [If applicable]
  
      Stakeholder Groups to Analyze:
      1. [List primary stakeholders]
      2. [List other relevant groups]
  
      For each stakeholder group, please suggest:
      - Their primary interests and concerns
      - Potential benefits and risks
      - Recommended engagement approach
      - Key messages for communication
  
      Special Considerations:
      [Note any sensitive aspects or specific relationship dynamics]
    `
  },
  {
    id: 5,
    icon: <Code size={28} />,
    title: 'Generate a JIRA story from my requirements',
    description: `
    Generate a well-structured JIRA user story.

    Context:
      - Project: [Insert project name and/or context]
      - Target user: [Who is this feature for?]
      - Problem: [Describe the underlying problem or need]
      - Current workflow: [Describe how things work now]

    Story details:
      - Epic link: [Parent epic name]
      - Dependencies: [List any dependent tasks or prerequisites]
      - Business value: [Describe why this is important]
      - Priority level: [High/Medium/Low]

    Please create a user story with:
      - Title using the format: "As a [user], I want [goal], so that [benefit]"
      - Acceptance criteria
      - Definition of Done
      - Story points estimate rationale
      - Technical considerations
      - Test scenarios
      - Any assumptions made

    Additional requirements:
      - Security considerations: [List any security requirements]
      - Compliance needs: [List any compliance requirements]
      - Performance expectations: [List any performance requirements]
    `
  },
  {
    id: 6,
    icon: <IconPencil />,
    title: 'Draft an engaging newsletter with my content',
    description: `
      Help me draft an engaging newsletter.
  
      Newsletter Basics:
      - Target audience: [Specify audience]
      - Purpose: [Informational/promotional]
      - Frequency: [Weekly/monthly/quarterly]
      - Tone: [Professional/casual/mix]
      - Length preference: [Paragraph/Section count]
  
      Content Elements Needed:
      - Main topic/theme: [Primary focus]
      - Key announcements: [List major updates/news]
      - Regular sections: [List standard sections to include with their content]
      - Call-to-action requirements: [Specify desired reader actions]
  
      Style Guidelines:
      - Brand voice guidelines: [Any specific tone/language requirements]
      - Formatting preferences: [Any specific layout requirements]
  
      Please structure the newsletter with:
      - Attention-grabbing headline
      - Engaging introduction
      - Organized body content
      - Clear sections and subheadings
      - Strong conclusion
      - Relevant call-to-action
    `
  },
  {
    id: 7,
    icon: <IconPencil />,
    title: 'Refactor my email to be effective for my audience',
    description: `
      Refactor this email to be more effective.
  
      Original Email:
      [Paste email content here]
  
      Refactoring Requirements:
      - Primary goal: [What's the main purpose of this email?]
      - Target recipients: [Who will read this?]
      - Desired tone: [Professional/friendly/urgent/formal]
      - Key message: [What's the one thing readers must understand?]
      - Desired action: [What should readers do after reading?]
  
      Areas to Optimize:
      - Length: [Shorter/longer/current length ok]
      - Clarity: [Any confusing points to address]
      - Structure: [Any items to re-organize]
      - Tone: [Any tone adjustments needed]
  
      Please provide:
      - Refactored email with improvements
      - Summary of key changes made
      - Subject line suggestion
    `
  },
  {
    id: 8,
    icon: <Bulb />,
    title: 'Brainstorm creative and practical ideas',
    description: `
      Brainstorm creative and practical ideas for the following challenge.
  
      Challenge Context:
      - Problem statement: [Describe the challenge/opportunity]
      - Current situation: [Describe the status quo]
      - Desired outcome: [What does success look like?]
      - Available resources: [List key resources/constraints]
      - Timeline: [When do ideas need to be implemented?]
  
      Parameters to Consider:
      - Budget constraints: [Any financial limitations]
      - Technical limitations: [Any technical constraints]
      - Organizational culture: [Any cultural factors to consider]
      - Risk tolerance: [High/medium/low]
      - Implementation complexity: [Preferred level of complexity]
  
      Specific Areas to Explore:
      - [List relevant areas to consider]
      - [List any specific aspects to focus on]
  
      Please Provide:
      - Range of ideas (from conservative to innovative)
      - Pros and cons for each idea
      - Resource requirements
      - Potential risks and mitigations
      - Success metrics for each idea
    `
  },
  {
    id: 9,
    icon: <IconPencil />,
    title: 'Improve my grammar and tone for my audience',
    description: `
      Help me improve the grammar, tone, and overall professionalism of this text.
  
      [Insert text here]
  
      Desired Improvements:
      - Target tone: [Formal/semi-formal/conversational]
      - Target audience: [Specify audience level and background]
      - Purpose of communication: [Inform/persuade/request/etc.]
      - Length constraints: [Keep same/shorten/expand]
  
      Style Preferences:
      - Phrases to avoid: [List any words/phrases to exclude]
      - Style requirements: [Any specific style rules]
  
      Please Provide:
      - Revised text with improved grammar and tone
      - List of significant changes made
      - Alternative phrasing suggestions for key points
      - Notes on any ambiguous elements that need clarification
    `
  },
  {
    id: 10,
    icon: <IconPencil />,
    title: 'Help me develop an executive summary',
    description: `
      Help me create an executive summary of this content.
      
      [Insert content]
  
      Summary Requirements:
      - Target length: [Desired paragraph count]
      - Target audience: [Who will read this summary?]
      - Primary purpose: [Decision-making/information/recommendation]
      - Technical level: [Expected reader expertise]
  
      Key Elements to Highlight:
      - Critical findings/insights
      - Financial implications
      - Risk factors
      - Strategic impact
      - Recommendations
      - Timeline considerations
  
      Please Structure with:
      - Opening hook/context
      - Main findings/points
      - Supporting evidence
      - Recommendations
      - Next steps/action items
      - Resource requirements
  
      Focus Areas:
      - Business impact
      - Strategic alignment
      - Resource implications
      - Risk assessment
      - Timeline and milestones
    `
  },
  {
    id: 11,
    icon: <IconPencil />,
    title: 'Simplify content for a less technical audience',
    description: `
      Help me convert this technical content into clear, simple language.
  
      Original Technical Content:
      [Insert content]
  
      Target Audience:
      - Background: [Audience expertise level]
      - Purpose: [Why they need to understand this]
      - Required comprehension level: [What they need to take away]
      - Usage context: [How they'll use this information]
  
      Conversion Preferences:
      - Desired complexity level: [Target reading level]
      - Technical terms to retain: [List essential technical terms]
      - Required context: [Background information to include]
      - Example preferences: [Type of examples that would resonate]
  
      Please Provide:
      - Simplified version of the content
      - Glossary of key terms (if needed)
      - Illustrative examples
      - Key takeaways in bullet points
  
      Additional Requirements:
      - Maintain accuracy of technical concepts
      - Include relevant real-world examples
      - Address common points of confusion
      - Provide context for complex ideas
    `
  },
  {
    id: 12,
    icon: <IconPencil />,
    title: 'Create a structured meeting agenda',
    description: `
      Help me create a structured and effective meeting agenda.
  
      Meeting Basics:
      - Purpose: [Main objective of the meeting]
      - Duration: [Length of meeting]
      - Participant information: [Attendee roles/numbers]
      - Meeting type: [Decision-making/information-sharing/brainstorming/review]
      - Format: [In-person/virtual]
  
      Context:
      - Previous meeting outcomes: [Key points from last meeting]
      - Current priorities: [Urgent items to address]
      - Required decisions: [Decisions needed from this meeting]
      - Additional materials: [e.g. confluence pages or other relevant context]
  
      Structure Needs:
      - Time allocation per topic: [How to divide available time]
      - Discussion format: [Presentation/open discussion/workshop]
      - Required preparation: [What attendees need to prepare]
      - Expected outcomes: [What should be achieved]
  
      Please create an agenda with:
      - Clear objectives
      - Prioritized topics
      - Time allocations
      - Owner for each item
      - Required preparations
      - Expected outputs
    `
  },
  {
    id: 13,
    icon: <Code size={28} />,
    title: 'Generate SQL queries',
    description: `
      Help me write a SQL query for the following requirement.
  
      Task Description:
      - Query purpose: [What information do you need?]
      - Database type: [e.g., MySQL, PostgreSQL, SQL Server]
  
      Database Structure:
      - Key tables: [List relevant tables]
      - Important fields: [List key columns]
      - Table relationships: [Describe how tables connect]
  
      Query Requirements:
      [Select relevant requirements]
      - Filtering conditions
      - Aggregations needed
      - Sorting preferences
      - Grouping requirements
      - Join operations
      - Date range handling
      - Performance constraints
      - Output format
  
      Special Considerations:
      - Performance: [Any speed requirements]
      - Data sensitivity: [Any fields to exclude]
      - NULL handling: [How to handle missing data]
  
      Please Provide:
      - Optimized SQL query
      - Explanation of the approach
      - Performance considerations
      - Alternative approaches
    `
  },
  {
    id: 14,
    icon: <Bulb />,
    title: 'Compare two companies in a table',
    description: `
      Help me create a detailed comparison analysis of these companies.
  
      Companies to Analyze:
      - Company A: [Name]
      - Company B: [Name]
  
      Comparison Categories:
      1. Business Model & Strategy
         - Revenue streams
         - Target markets
         - Competitive advantages
         - Growth strategy
  
      2. Financial Performance
         - Key financial metrics
         - Growth rates
  
      3. Market Position
         - Market share
         - Brand strength
         - Geographic presence
         - Customer base
  
      4. Operations & Scale
         - Operating model
         - Infrastructure
  
      Please Provide:
      - Tabular comparison with the company names as headers, comparing one variable per row
      - Side-by-side comparison
      - Key differentiators
      - Relative strengths/weaknesses
  
      Additional Requirements:
      - Include relevant metrics and KPIs
    `
  },
  {
    id: 15,
    icon: <Bulb />,
    title: 'Generate a mathematical formula',
    description: `
      Create a mathematical formula to achieve the following task:
  
      Problem Context
      - Problem Description: [Describe what you are trying to calculate]
      - Reasoning: [Why are you calculating this?]
      - Variables to Include: [List variables and what they represent]
      - Units of Measurement: [Specify any units if applicable]
  
      Please Provide:
      - The correct mathematical formula and justification as to why this is appropriate
      - Explanation of variables and constants
      - Implementation guidance
    `
  }
]

const welcomeMsgSchema = {
  header: 'Ask everyday questions',
  hideForSm: false,
  description: '',
  enableClick: true,
  noBorder: false,
  isLink: false
}

const aliIcon = `${staticAssetsPath}/images/icons/ali_icon.svg`
const loaderElement = `<div class="dot-pulse dot-pulse-primary"></div>`
const loaderElementSecondary = `<div class="dot-pulse dot-pulse-secondary"></div>`
const loaderElementInfo = `<div class="dot-pulse dot-pulse-info"></div>`
const loaderElementMidnight = `<div class="dot-pulse dot-pulse-midnight"></div>`
const loaderElementPurple = `<div class="dot-pulse dot-pulse-purple"></div>`
const loaderElementBlue = `<div class="dot-pulse dot-pulse-blue"></div>`
const loaderElementBeige = `<div class="dot-pulse dot-pulse-beige"></div>`
const defaultErrorMsg = `Oops, it seems my virtual gears are having a cozy tea break ☕. Please try again after some time, and I'll be back with a freshly brewed answer for you!`
const tokenExpirerMsg = `It seems we have reached token limit for this conversation. Please start a new conversation to continue.`
const welcomeMsgVisibilityWidth = 920
const noResultsFound = `Sorry, I couldn't find any results for your query. Please try again with a different query.`
const sqlError = `Sorry, we experienced an issue with the SQL query generation based on your prompt. Please try a different prompt or rephrase your query`
const requestAbortedMsg =
  '<div class="chat-aborted-msg italic p-2.5 text-sm">The response was forcefully stopped before completion.</div>'

const imageCountLimit = 10
const imageSizeLimit = 20 // 20MB
const maxImageLimit = 10
const maxImgError = `Maximum of ${maxImageLimit} images are allowed in a single conversation.`
const imgLimitError = `You can upload only ${imageCountLimit} image per request.`
const imgSizeError = `Image size exceeds the limit of ${imageSizeLimit}MB`
const fileSizeError = (size) =>
  `${size < 1 ? 'Excel' : size === 1 ? 'CSV file' : 'File'} size exceeds the limit of ${
    size < 1 ? `${size * 1000}KB` : `${size}MB`
  }`
const uploadErrorMessage = 'You can only upload images or files at a time'
const imgChatError = 'Only images are allowed in this chat. Please upload a valid image.'

const ERROR_MESSAGES = {
  404: "Oops! I couldn't find what you're looking for. Can you try again?",
  500: "It seems there's a problem at my end. Please try again later.",
  401: 'You need to be logged in to access this feature. Please log in and try again.',
  403: "Sorry, you don't have permission to access this. Please contact support.",
  400: 'It looks like there was an issue with your request. Can you check and try again?',
  408: 'The request took too long to process. Please try again.'
}

function getErrorMessage(errorCode) {
  return ERROR_MESSAGES[errorCode] || `Sorry! I'm unable to answer your query`
}

const getBotLoader = (theme, type) => {
  let loadElement = loaderElement
  if (theme === 'info') {
    loadElement = loaderElementInfo
  } else if (theme === 'secondary' || theme === 'orange') {
    loadElement = loaderElementSecondary
  } else if (theme === 'midnight') {
    loadElement = loaderElementMidnight
  } else if (theme === 'purple') {
    loadElement = loaderElementPurple
  } else if (theme === 'blue') {
    loadElement = loaderElementBlue
  } else if (theme === 'beige') {
    loadElement = loaderElementBeige
  }
  return type && type === 'text'
    ? loadElement
    : {
        ...messageSchema,
        response: [
          {
            ...responseSchema,
            content: loadElement
            // type: 'loader',
            // timestamp: new Date().getTime()
          }
        ]
      }
}

const getBotLoaderText = () => {
  return loaderElement
}

const documentScanMessage = {
  ...messageSchema,
  response: [
    {
      ...responseSchema,
      content: `I've received your attachment(s). How can I help?`
    }
  ]
}

const smartDataMessage = (topic) => {
  return {
    ...messageSchema,
    response: [
      {
        ...responseSchema,
        content: `I've had a read of your chosen topic, ${topic}. How can I help?`
      }
    ]
  }
}

const modeStartMessage = {
  ...messageSchema,
  type: 'started'
}

const modeEndMessage = {
  ...messageSchema,
  type: 'ended'
}

const validFileInput = ['.pdf', '.docx', '.txt', '.pptx', '.xlsx', '.csv', '.html']
const validImageInput = ['.jpg', '.jpeg', '.png', '.gif', '.webp']

const dotPulse = '&lt;span class=&quot;single-pulse&quot;/&gt;'
const dotPulseHtml = `<span class="single-pulse"></span>`

const CHAT_MSG_PER_PAGE = 50

const getStructuredMessages = async (messages = []) => {
  const structuredMessages = []
  let lastMsgHasFiles = false
  messages.forEach((message, i) => {
    const currFiles = message?.resources || []
    const prevFiles = messages[i - 1]?.resources || []
    const hasImageFiles = hasImages(currFiles)
    if (currFiles.length > 0 && !hasImageFiles) {
      if (prevFiles.length <= 0) {
        //curr msg has file & prev msg has no file
        structuredMessages.push(modeStartMessage)
      } else if (
        currFiles.length > 0 &&
        prevFiles.length > 0 &&
        !areArraysEqual(currFiles, prevFiles)
      ) {
        //curr msg has file & prev msg has file and if files are not equal
        structuredMessages.push(modeEndMessage, modeStartMessage)
      } else if (
        currFiles.length <= 0 &&
        prevFiles.length > 0 &&
        !areArraysEqual(currFiles, prevFiles)
      ) {
        //curr msg has no file & prev msg has file and if files are not equal
        structuredMessages.push(modeEndMessage)
      }
    }
    lastMsgHasFiles = currFiles.length > 0 && !hasImageFiles
    structuredMessages.push({
      ...messageSchema,
      messageId: message.messageId,
      role: message.role,
      files: message.resources || [],
      response: message.content.map((content) => {
        return { ...responseSchema, content: content.message, model: content.model }
      })
    })
  })

  if (lastMsgHasFiles) structuredMessages.push(modeEndMessage)

  return structuredMessages
}

const conversationStyleOptions = [
  {
    id: 1,
    name: 'Balanced',
    value: 0.3,
    topP: 0.5,
    position: 'center',
    desc: 'Default, well-rounded responses'
  },
  {
    id: 2,
    name: 'Creative',
    value: 1,
    topP: 0.95,
    position: 'left',
    desc: 'Imaginative, more varied responses'
  },

  {
    id: 3,
    name: 'Precise',
    value: 0,
    topP: 0.2,
    position: 'right',
    desc: 'Clear, more focused responses'
  }
]

const copyIconString = `<svg  width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>`

const maximizeIconString = `<svg width="18" height="18" viewBox="0 0 16 16" fill="none" ><path fill-rule="evenodd" clip-rule="evenodd" d="M10.127 2.157c.4 0 .67 0 .877.018.2.016.297.045.36.078a.88.88 0 0 1 .382.382c.033.064.062.16.078.36.017.208.018.477.018.877v.794a.525.525 0 0 0 1.05 0v-.814c0-.375 0-.687-.021-.942a2 2 0 0 0-.19-.752 1.93 1.93 0 0 0-.84-.84 2 2 0 0 0-.752-.19c-.255-.02-.567-.02-.94-.02h-.817a.525.525 0 1 0 0 1.05h.794zM2.158 9.332a.525.525 0 1 0-1.05 0v.815c0 .374 0 .686.021.941.022.266.069.516.19.752.184.362.478.656.84.84.237.121.486.168.752.19.255.02.567.02.941.02h.815a.525.525 0 0 0 0-1.05h-.794c-.4 0-.67 0-.876-.017-.201-.016-.297-.045-.36-.078a.88.88 0 0 1-.383-.382c-.033-.063-.062-.16-.078-.36a12 12 0 0 1-.018-.877zm1.715-7.175c-.4 0-.67 0-.876.018-.201.016-.297.045-.36.078a.88.88 0 0 0-.383.382c-.033.064-.062.16-.078.36-.017.208-.018.477-.018.877v.794a.525.525 0 1 1-1.05 0v-.814c0-.375 0-.687.021-.942.022-.266.069-.515.19-.752.184-.362.478-.656.84-.84.237-.121.486-.168.752-.19.255-.02.567-.02.941-.02h.815a.525.525 0 1 1 0 1.05h-.794zm7.969 7.175a.525.525 0 0 1 1.05 0v.815c0 .374 0 .686-.021.941a2 2 0 0 1-.19.752 1.92 1.92 0 0 1-.84.84 2 2 0 0 1-.752.19c-.255.02-.567.02-.941.02h-.815a.525.525 0 0 1 0-1.05h.794c.4 0 .67 0 .877-.017.2-.016.297-.045.36-.078a.88.88 0 0 0 .382-.382c.033-.063.062-.16.078-.36.017-.208.018-.476.018-.877z" fill='#fff'></path></svg>`

const downloadIconString = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>`

const chevronLeftIconString = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>`

const chevronRightIconString = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>`

const langToExtensionMap = {
  javascript: 'js',
  typescript: 'ts',
  python: 'py',
  java: 'java',
  csharp: 'cs',
  cpp: 'cpp',
  c: 'c',
  ruby: 'rb',
  go: 'go',
  rust: 'rs',
  swift: 'swift',
  kotlin: 'kt',
  php: 'php',
  html: 'html',
  css: 'css',
  scss: 'scss',
  less: 'less',
  json: 'json',
  xml: 'xml',
  yaml: 'yaml',
  yml: 'yml',
  markdown: 'md',
  sql: 'sql',
  bash: 'sh',
  shell: 'sh',
  powershell: 'ps1',
  dockerfile: 'Dockerfile',
  graphql: 'graphql',
  r: 'r',
  scala: 'scala',
  perl: 'pl',
  lua: 'lua',
  dart: 'dart',
  jsx: 'jsx',
  tsx: 'tsx',
  vue: 'vue',
  svelte: 'svelte',
  'excel-formula': 'xlsx',
  text: 'txt'
}

const getFileExtension = (lang) => {
  if (!lang || lang === 'undefined' || lang === 'Unknown') return 'txt'
  return langToExtensionMap[lang.toLowerCase()] || 'txt'
}

const modeStatusArray = ['started', 'ended']

const betaModelEngine = (isBetaUser) => (isBetaUser ? 'o4-mini' : 'o1-mini')

const getBrowserName = () => {
  const userAgent = navigator.userAgent.toLowerCase()

  switch (true) {
    case userAgent.includes('chrome') && !userAgent.includes('edg'):
      return 'chrome'
    case userAgent.includes('firefox'):
      return 'firefox'
    case userAgent.includes('safari') && !userAgent.includes('chrome'):
      return 'safari'
    case userAgent.includes('edg'):
      return 'edge'
    default:
      return 'unknown'
  }
}

const getVoice = (voiceList) => {
  const browser = getBrowserName()
  switch (browser.toLowerCase()) {
    case 'chrome':
      return voiceList.find((voice) => voice.name === 'Microsoft Zira - English (United States)') //'Google UK English Female') commenting this as its not properly supporting on chrome
    case 'firefox':
      return voiceList[0]
    case 'safari':
      return voiceList[0]
    case 'edge':
      return voiceList.find(
        (voice) => voice.name === 'Microsoft Libby Online (Natural) - English (United Kingdom)'
      )
    default:
      return voiceList[0]
  }
}

const attachOptions = [
  {
    id: 'files',
    name: 'Add files',
    icon: () => <IconFiles size={20} />
  },
  {
    id: 'images',
    name: 'Add images',
    icon: () => <IconPolaroid size={20} />
  },
  {
    id: 'web-search',
    name: 'Web search',
    icon: () => <IconWorldSearch size={20} />
  },
  {
    id: 'add-sources',
    name: 'Add sources',
    icon: () => <IconFolderRoot size={20} />
  }
]

const convertFileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      if (reader.result) {
        resolve(reader.result.toString()) // Base64 string
      } else {
        reject(new Error('File reading failed'))
      }
    }
    reader.onerror = () => {
      reject(new Error('File reading has failed'))
    }
    reader.readAsDataURL(file)
  })
}

const defaultAssistant = 'Centrali' // Default assistant to be set

const mainChatModels = ['gpt-5.5', 'gpt-5-mini']

const getModelDisplayName = (models, name) => {
  return models?.find((model) => model.name === name)?.displayName || name
}

const getRandomGreeting = () => {
  const greetingMessages = [
    'what would you like to explore today?',
    'ready when you are.',
    'what’s on your mind today?',
    'how can I help you get started?',
    'what brings you here today?',
    'got something new to dive into?',
    'let’s get started — what’s the plan?',
    'what are we working on today?',
    'what idea are we bringing to life today?',
    'got something you’re curious about?',
    'let’s brainstorm — what’s in your mind?',
    'want to explore something new today?',
    'I’m all set — what should we think through today?',
    'systems online and ready. what’s your first query?',
    'I’m tuned in. what would you like me to assist with?',
    'let’s co-create. what do we start with?'
  ]

  const idx = Math.floor(Math.random() * greetingMessages.length)
  return greetingMessages[Math.max(0, Math.min(idx, greetingMessages.length - 1))]
}

const removeTempMessages = (messages) => {
  return messages.filter(
    (msg) =>
      !msg.id?.toString().startsWith('temp-') && !msg.parentId?.toString().startsWith('temp-')
  )
}

// MCP Server Names
export const MCP_SERVER_NAMES = {
  WEB_SEARCH: 'WebSearchServer',
  SMART_INSIGHTS: 'SmartInsights',
  PERSONAL_UNSTRUCTURED: 'PersonalUnstructured',
  SNOWFLAKE: 'StructuredData',
  CODE_INTERPRETER: 'CodeInterpreterMcpServer'
}

// Tool Configuration Modes
export const ToolConfigMode = {
  AUTO: 'Auto',
  FORCE_SPECIFIC: 'ForceSpecific',
  REQUIRED_ANY: 'RequiredAny'
}

// AG UI Protocol Event Types
export const AG_UI_EVENT_TYPES = {
  RUN_STARTED: 'RUN_STARTED',
  TEXT_MESSAGE_START: 'TEXT_MESSAGE_START',
  TEXT_MESSAGE_CONTENT: 'TEXT_MESSAGE_CONTENT',
  TEXT_MESSAGE_END: 'TEXT_MESSAGE_END',
  TOOL_CALL_START: 'TOOL_CALL_START',
  TOOL_CALL_ARGS: 'TOOL_CALL_ARGS',
  TOOL_CALL_END: 'TOOL_CALL_END',
  TOOL_CALL_RESULT: 'TOOL_CALL_RESULT',
  RUN_FINISHED: 'RUN_FINISHED',
  REASONING_START: 'REASONING_START',
  REASONING_CONTENT: 'REASONING_CONTENT',
  REASONING_END: 'REASONING_END',
  RUN_ERROR: 'RUN_ERROR'
}

export const MAX_DROP_FILES = 100

const filterChatModels = (models) => {
  return (
    models?.filter((model) => !model.name.includes('o4-mini') && !model.name.includes('gpt-4')) ||
    []
  )
}

export {
  responseSchema,
  messageSchema,
  getBotLoader,
  getBotLoaderText,
  aliIcon,
  defaultErrorMsg,
  tokenExpirerMsg,
  documentScanMessage,
  modeStartMessage,
  modeEndMessage,
  promptGuideSrc,
  everyDayQuesArray,
  workRelatedQuesArray,
  welcomePageSuggestions,
  noResultsFound,
  welcomeMsgSchema,
  welcomeMsgVisibilityWidth,
  validFileInput,
  validImageInput,
  smartDataMessage,
  sqlError,
  dotPulse,
  dotPulseHtml,
  CHAT_MSG_PER_PAGE,
  getStructuredMessages,
  conversationStyleOptions,
  copyIconString,
  maximizeIconString,
  downloadIconString,
  chevronLeftIconString,
  chevronRightIconString,
  langToExtensionMap,
  getFileExtension,
  modeStatusArray,
  getErrorMessage,
  betaModelEngine,
  getVoice,
  attachOptions,
  convertFileToBase64,
  maxImgError,
  imgLimitError,
  imgSizeError,
  fileSizeError,
  uploadErrorMessage,
  imageCountLimit,
  imageSizeLimit,
  maxImageLimit,
  imgChatError,
  defaultAssistant,
  mainChatModels,
  getModelDisplayName,
  getRandomGreeting,
  removeTempMessages,
  filterChatModels,
  requestAbortedMsg
}
